import "./AppPaySignatories.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { CheckSignatory, Company, CheckCompany, isCheckError, CheckResponseError } from "winslow-core";
import { 
    apiCheckGetSignatories, 
    apiCheckCreateSignatory,
} from "../../../services/PayrollService";
import { Button, Alert, AlertDescription, Label } from "../../../ds2/components";
import { Plus } from "lucide-react";
import { ActionLink } from "../../../ds2/components/ActionLink";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";
import { isValidEmail } from "winslow-core";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";

type AppPaySignatoriesProps = {
  company: Company | null;
  checkCompany: CheckCompany | null;
};

export const AppPaySignatories = ({ company, checkCompany }: AppPaySignatoriesProps) => {
    const dispatch = useDispatch();
    const uiStates = useSelector(selectUIStates);
    const storeUser = useSelector(selectUser);

    const checkCompanyId = company?.checkCompanyId || checkCompany?.id;

    const [checkSignatories, setCheckSignatories] = useState<CheckSignatory[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);
    const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(false);

    const [formData, setFormData] = useState<Omit<CheckSignatory, "id">>({
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        title: "",
    });

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Payroll Configuration"}));
        loadCheckSignatories();
    }, [storeUser, checkCompanyId]);

    // Validate email whenever it changes
    useEffect(() => {
        const trimmedEmail = formData.email.trim();
        if (trimmedEmail.length > 0) {
            setIsEmailInvalid(!isValidEmail(trimmedEmail));
        } else {
            setIsEmailInvalid(false);
        }
    }, [formData.email]);

    const loadCheckSignatories = async () => {
        if (!checkCompanyId) return;
        
        setLoading(true);
        try {
            const apiResponse = await apiCheckGetSignatories(checkCompanyId);
            setCheckResponseError(apiResponse?.checkResponseError);
            
            if (!apiResponse) {
                toast("Error loading signatories", toastFail);
                setCheckSignatories([]);
                return;
            }

            if (isCheckError(apiResponse)) {
                toast("Error loading signatories", toastFail);
                setCheckSignatories([]);
                return;
            }

            const signatories = apiResponse.data;
            setCheckSignatories(signatories || []);
        } catch (error) {
            console.error("[AppPaySignatories] Error loading signatories:", error);
            toast("Error loading signatories", toastFail);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: "",
            middle_name: "",
            last_name: "",
            email: "",
            title: "",
        });
        setShowAddForm(false);
        setCheckResponseError(undefined);
        setIsEmailInvalid(false);
    };

    const handleAddClick = () => {
        resetForm();
        setShowAddForm(true);
    };


    const handleSave = async () => {
        setCheckResponseError(undefined);
        
        if (!checkCompanyId) {
            toast("Check company ID not found", toastFail);
            return;
        }

        if (!formData.first_name.trim()) {
            toast("First name is required", toastFail);
            return;
        }

        if (!formData.last_name.trim()) {
            toast("Last name is required", toastFail);
            return;
        }

        if (!formData.email.trim() || !isValidEmail(formData.email.trim())) {
            toast("Valid email is required", toastFail);
            return;
        }

        if (!formData.title.trim()) {
            toast("Title is required", toastFail);
            return;
        }

        try {
            const apiResponse = await apiCheckCreateSignatory(checkCompanyId, formData);
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse) {
                toast("Error saving signatory", toastFail);
                return;
            }

            if (isCheckError(apiResponse)) {
                const errorMsg = apiResponse.checkResponseError?.errorMessage || "Error saving signatory";
                toast(`Error saving signatory (${errorMsg})`, toastFail);
                return;
            }

            const createdSignatory = apiResponse.data;

            if (createdSignatory) {
                toast("Signatory saved successfully", toastSuccess);
                setCheckResponseError(undefined);
                await loadCheckSignatories();
                resetForm();
            } else {
                toast("Error saving signatory", toastFail);
            }
        } catch (error) {
            console.error("[AppPaySignatories] Error saving signatory:", error);
            toast("Error saving signatory", toastFail);
        }
    };

    return (
        <div className="app-signatories">
            <AppCheckErrorMessage checkResponseError={checkResponseError} />
            <div className="app-signatories-header">
                {!showAddForm && (
                    <ActionLink
                        label="Add Signatory"
                        leftIcon={<Plus size={16} />}
                        onClick={handleAddClick}
                    />
                )}
            </div>

            {showAddForm && (
                <div className="app-signatories-form">
                    <div className="app-signatories-form-header">
                        <h3>Add Signatory</h3>
                    </div>
                    <div className="app-signatories-form-content">
                        <div className="app-custom-property-label">
                            First Name *
                        </div>
                        <div className="app-custom-property-value">
                            <input
                                type="text"
                                placeholder="Enter first name"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            />
                        </div>

                        <div className="app-custom-property-label">
                            Middle Name
                        </div>
                        <div className="app-custom-property-value">
                            <input
                                type="text"
                                placeholder="Enter middle name (optional)"
                                value={formData.middle_name}
                                onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                            />
                        </div>

                        <div className="app-custom-property-label">
                            Last Name *
                        </div>
                        <div className="app-custom-property-value">
                            <input
                                type="text"
                                placeholder="Enter last name"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            />
                        </div>

                        <Label 
                            required
                            invalid={isEmailInvalid}
                            invalidReason="Invalid email address"
                        >
                            Email
                        </Label>
                        <div className="app-custom-property-value">
                            <input
                                type="email"
                                placeholder="Enter email address"
                                value={formData.email}
                                onChange={(e) => {
                                    setFormData({ ...formData, email: e.target.value });
                                }}
                            />
                        </div>

                        <div className="app-custom-property-label">
                            Title *
                        </div>
                        <div className="app-custom-property-value">
                            <input
                                type="text"
                                placeholder="Enter title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="app-signatories-form-actions">
                        <Button
                            label="Cancel"
                            variant="secondary"
                            onClick={resetForm}
                        />
                        <Button
                            label="Create"
                            variant="primary"
                            onClick={handleSave}
                            disabled={!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim() || !isValidEmail(formData.email.trim()) || !formData.title.trim()}
                        />
                    </div>
                </div>
            )}

            {!showAddForm && (
                <>
                    {loading ? (
                        <div className="app-signatories-loading">
                            Loading signatories...
                        </div>
                    ) : checkSignatories.length === 0 ? (
                        <div className="app-signatories-empty">
                            <p>No signatories found. Click "Add Signatory" to create one.</p>
                        </div>
                    ) : (
                        <div className="app-signatories-table-container">
                            <table className="app-signatories-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Title</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checkSignatories.map((signatory) => (
                                        <tr key={signatory.id} className="app-signatories-row">
                                            <td className="app-signatories-name-cell">
                                                <strong>
                                                    {[signatory.first_name, signatory.middle_name, signatory.last_name]
                                                        .filter(Boolean)
                                                        .join(" ")}
                                                </strong>
                                            </td>
                                            <td className="app-signatories-email-cell">
                                                {signatory.email || "-"}
                                            </td>
                                            <td className="app-signatories-title-cell">
                                                {signatory.title || "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

